import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiFetch, ApiError, Me } from '../api/client';
import PortalLoading from './PortalLoading';

const moduleLoaders: Record<string, () => Promise<{ default: React.ComponentType }>> = {
  'tech-inventory': () => import('../modules/tech-inventory/TechInventory'),
  'qa-tracker': () => import('../modules/qa-tracker/QaTracker'),
  'data-mapping': () => import('../modules/data-mapping/DataMapping'),
  'action-tracker': () => import('../modules/action-tracker/ActionTracker'),
  'asana-board': () => import('../modules/asana-board/AsanaBoard'),
  'open-items': () => import('../modules/asana-board/OpenItemsModule'),
  'ui-signoff': () => import('../modules/ui-signoff/UiSignoff'),
  uat: () => import('../modules/uat/Uat'),
  'mi-sherpa': () => import('../modules/mi-sherpa/MiSherpa'),
  'pr-review': () => import('../modules/pr-review/PrReview'),
};

const moduleLabels: Record<string, string> = {
  'tech-inventory': 'Technical Inventory',
  'qa-tracker': 'Q&A Tracker',
  'data-mapping': 'Data Mapping',
  'action-tracker': 'Action Tracker',
  'asana-board': 'Project Delivery Board',
  'open-items': 'Open Items',
  'ui-signoff': 'UI Sign-off',
  uat: 'UAT',
  'mi-sherpa': 'MI Sherpa',
  'pr-review': 'PR Review',
};

// Modules that are visible only to staff users, even when the client has the
// module enabled. External client-portal users never see them in the sidebar,
// on the Overview page, or when direct-navigating to /:clientSlug/:moduleCode
// (ModuleHost gates on the filtered list too). Backend enforces the same rule.
// pr-review is also left out of /portal/me for staff who are not the client's designated reviewers.
const STAFF_ONLY_MODULES = new Set(['tech-inventory', 'pr-review']);

export default function PortalShell() {
  const { clientSlug } = useParams();
  const { getToken, signOut, staffAccount, client } = useAuth();
  const username = staffAccount?.username ?? client?.email ?? '';
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const t = await getToken();
        const data = await apiFetch<Me>('/portal/me', t);
        setMe(data);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) setUnauthorized(true);
        else setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [getToken]);

  const currentClient = useMemo(() => me?.clients.find((c) => c.slug === clientSlug), [me, clientSlug]);

  // Not (or no longer) authenticated — bounce to login.
  if (unauthorized) return <Navigate to="/login" replace />;
  if (error) return (
    <div style={{ padding: 32 }}>
      <h2 style={{ color: 'var(--mi-navy)' }}>Something went wrong</h2>
      <p style={{ color: 'var(--mi-muted)' }}>We couldn’t load your workspace. Please try again in a moment.</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={() => window.location.reload()}>Retry</button>
        <button onClick={signOut} style={{ background: 'transparent', border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 12px' }}>Sign out</button>
      </div>
    </div>
  );
  if (!me) return <PortalLoading />;
  if (!currentClient) return <Navigate to="/" replace />;

  // Hide staff-only modules from external client users. Staff still see them
  // labelled so it's obvious they're admin-scoped.
  const rawModules = currentClient.modules ?? [];
  const modules = me.user.role === 'staff'
    ? rawModules
    : rawModules.filter((m) => !STAFF_ONLY_MODULES.has(m));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: '100vh' }}>
      <aside style={{ background: 'var(--mi-navy)', color: 'white', padding: '1rem' }}>
        <img src="/MI-Logo-DarkBackground.png" alt="Mount Insights" style={{ width: '100%', maxWidth: 120, height: 'auto', marginBottom: 20, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        <div style={{ fontSize: 12, color: '#9fb3c4', textTransform: 'uppercase' }}>{currentClient.name}</div>
        <nav style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <NavLink to={`/${clientSlug}`} end style={navLinkStyle}>Overview</NavLink>
          {modules.map((m) => (
            <NavLink key={m} to={`/${clientSlug}/${m}`} style={navLinkStyle}>
              <span>{moduleLabels[m] ?? m}</span>
              {STAFF_ONLY_MODULES.has(m) && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#9fb3c4' }}>(admin only)</span>
              )}
              {currentClient.previewModules?.includes(m) && (
                <span style={{ marginLeft: 6, fontSize: 10, color: '#f3b27a' }} title="Visible to Mount Insights staff only. Client users do not see this module.">(staff preview)</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div style={{ marginTop: 'auto', position: 'absolute', bottom: 16, fontSize: 12 }}>
          <div>{username}</div>
          <button onClick={signOut} style={{ marginTop: 8, background: 'transparent', color: 'white', border: '1px solid #9fb3c4' }}>
            Sign out
          </button>
        </div>
      </aside>
      {/* minWidth 0 is load-bearing. As a flex child, main defaults to
          min-width:auto, so it cannot shrink below its content — a module
          wider than the viewport grows main and scrolls the whole page
          sideways instead of scrolling inside its own panel. Measured with
          the delivery board's Timeline at a 760px viewport: main 1101px,
          document 1341px. With minWidth 0: main 520px, document 760px, and
          the Timeline still scrolls internally. */}
      <main style={{ padding: '2rem', minWidth: 0 }}>
        <Suspense fallback={<div>Loading module…</div>}>
          <Outlet context={{ clientSlug, clientId: currentClient.id, clientName: currentClient.name, userRole: me.user.role, userId: me.user.id, modules, moduleLoaders }} />
        </Suspense>
      </main>
    </div>
  );
}

const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  color: isActive ? 'var(--mi-orange)' : 'white',
  padding: '6px 8px',
  borderRadius: 6,
  background: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
});

export { moduleLoaders };
